import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { api } from '../api/client';
import { naviguer } from '../App';
import { ModeBadge, Solde, Montant } from '../components/Montant';
import { chf, dateCourte, LIBELLE_MOYEN, moisLabel } from '../utils/format';
import type { Tableau } from '../types';

// Tableau de bord : ce qui est dû, ce qui est rentré ce mois, ce qui attend.
export function TableauPage() {
  const [data, setData] = useState<Tableau | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getTableau().then(setData).catch((e) => setError(e instanceof Error ? e.message : 'Échec du chargement'));
  }, []);

  if (error) return <div className="page"><div className="badge badge-err" style={{ display: 'block' }}>{error}</div></div>;
  if (!data) return <div className="page"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <span className="page-title">Tableau de bord</span>
          <p className="page-meta">{moisLabel(data.mois)} · {data.nb_actifs} participant·e·s en mesure SISP ({data.nb_cash} cash, {data.nb_facture} facture)</p>
        </div>
        <button className="btn btn-accent" onClick={() => naviguer('participants')}>Voir les participant·e·s</button>
      </div>

      <div className="stats-grid mb-24">
        <div className="stat-card">
          <div className="stat-value montant is-doit">{chf(data.total_du)}</div>
          <div className="stat-label">Total dû par {data.nb_en_dette} personne{data.nb_en_dette > 1 ? 's' : ''} · {data.nb_au_dessus_seuil} au-dessus de {chf(data.seuil)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value montant">{chf(data.encaisse_mois)}</div>
          <div className="stat-label">Encaissé ce mois · {data.nb_paiements_mois} paiement{data.nb_paiements_mois > 1 ? 's' : ''}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.seances_mois}</div>
          <div className="stat-label">Séances suivies ce mois · {chf(data.du_mois)} à percevoir</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.factures_envoyees}</div>
          <div className="stat-label">Factures envoyées en attente · {chf(data.montant_factures_envoyees)}{data.factures_brouillon ? ` · ${data.factures_brouillon} brouillon${data.factures_brouillon > 1 ? 's' : ''}` : ''}</div>
        </div>
      </div>

      <div className="dash-grid">
        <div>
          <h3 className="section-label mb-12">Soldes les plus élevés</h3>
          {data.soldes.length === 0 ? (
            <div className="empty-state">Personne ne doit rien. Belle journée.</div>
          ) : (
            <table className="table table-compact">
              <thead><tr><th>Participant·e</th><th>Mode</th><th className="num">Solde</th><th>Dernier paiement</th><th></th></tr></thead>
              <tbody>
                {data.soldes.map((s) => (
                  <tr key={s.id} className="is-clickable" onClick={() => naviguer('participants', s.id)}>
                    <td className="font-medium">{s.nom}<span className="cell-sub">{s.referent ?? '—'}</span></td>
                    <td><ModeBadge mode={s.mode} /></td>
                    <td className="num"><Solde valeur={s.solde} /></td>
                    <td className="text-sm">{dateCourte(s.dernier_paiement)}</td>
                    <td><ArrowRight className="icon-sm text-ink-3" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {data.a_confirmer.length > 0 && (
            <>
              <h3 className="section-label mb-12 mt-24">Mode de paiement à confirmer</h3>
              <div className="flex flex-wrap gap-8">
                {data.a_confirmer.map((p) => (
                  <button key={p.id} className="chip" onClick={() => naviguer('participants', p.id)}>{p.nom} · {p.mode === 'cash' ? 'cash ?' : 'facture ?'}</button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="panel">
          <div className="panel-header"><h3>Derniers paiements</h3></div>
          <div className="panel-body">
            {data.derniers_paiements.length === 0 ? (
              <p className="text-sm text-ink-3">Aucun paiement saisi pour l'instant.</p>
            ) : (
              <table className="table table-compact">
                <tbody>
                  {data.derniers_paiements.map((p) => (
                    <tr key={p.id} className="is-clickable" onClick={() => naviguer('participants', p.participant_id)}>
                      <td className="text-sm tabular-nums">{dateCourte(p.date)}</td>
                      <td className="font-medium">{p.participant_nom}<span className="cell-sub">{LIBELLE_MOYEN[p.moyen]}{p.note ? ` · ${p.note}` : ''}</span></td>
                      <td className="num"><Montant valeur={p.montant} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
